// Home.js
import React, { useEffect, useState, useMemo, useRef } from "react";
import "./Home.css";
import "./Details.css";
import Navbar from "./navbar";
import BarChart from "./DataCenter/BarChart";
import PeriodFilter from "./DataCenter/PeriodFilter";
import { useAuth } from "../auth/AuthContext";
import { busyErrorFrom, useDataApiBusy } from "../services/dataApiBusy";
import BusyNotice from "./DataCenter/BusyNotice";
import {
  revenueOverTime,
  salesByCategory,
  partySizeByHour,
} from "./DataCenter/homeChartPresets";

// Static constants (don’t need to be recreated on every render)
const REDIR_BASE = "https://mydata.tablemate.net/redir.php?url=";
const TILES_REFRESH_MS = 60 * 1000;

const formatClock = (d) =>
  d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

// Restaurant's calendar day, so "Today" stats don't follow the server's UTC clock
const localToday = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/* ---------- Star control ---------- */
function Stars({ value = 0, onChange }) {
  const [hover, setHover] = useState(null);

  const activeValue = hover ?? value;

  return (
    <div style={{ display: "flex", gap: 4 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
          onClick={() => onChange?.(i)}
          style={{
            fontSize: 20,
            cursor: "pointer",
            color: activeValue >= i ? "#ffbf00" : "#ccc",
          }}
          aria-label={`${i} star${i > 1 ? "s" : ""}`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function Home() {
  const pageTitle = "Dashboard";

  const {
    user,
    tenantServerUrl,
    primaryRestaurantId,
    selectedRestaurantId,
    accessToken,
  } = useAuth();

  const bearerToken = useMemo(() => {
    if (!accessToken) return null;
    return accessToken.startsWith("Bearer ")
      ? accessToken
      : `Bearer ${accessToken}`;
  }, [accessToken]);

  const [isQuestionDataLoading, setIsQuestionDataLoading] = useState(true);

  // All-time stats
  const [allTimeRevenue, setAllTimeRevenue] = useState(0.0);
  const [allTimeCustomers, setAllTimeCustomers] = useState(0);
  const [todayRevenue, setTodayRevenue] = useState(0.0);
  const [todayCustomers, setTodayCustomers] = useState(0);
  const [allRating, setAllRating] = useState(3);
  const [employeeOfMonth, setEmployeeOfMonth] = useState("Loading...");
  const [allTimeMostPopularOrder, setAllTimeMostPopularOrder] =
    useState("Loading...");
  const [todayMostPopularOrder, setTodayMostPopularOrder] =
    useState("Loading...");

  // Tiles refresh every minute while the tab is visible; later loads keep the old numbers on screen.
  const [tilesTick, setTilesTick] = useState(0);
  const [tilesUpdatedAt, setTilesUpdatedAt] = useState(null);
  const [tilesRefreshing, setTilesRefreshing] = useState(false);
  const tilesLoadedFor = useRef(null);
  const tilesUpdatedAtRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") setTilesTick((t) => t + 1);
    }, TILES_REFRESH_MS);
    const onVisible = () => {
      const last = tilesUpdatedAtRef.current;
      if (document.visibilityState === "visible" && (!last || Date.now() - last.getTime() >= TILES_REFRESH_MS)) {
        setTilesTick((t) => t + 1);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // 🔁 date/period selection from PeriodFilter
  const [periodSelection, setPeriodSelection] = useState(null);

  const periodFilter = [
    { title: "Today", value: "today" },
    { title: "This Week", value: "this_week" },
    { title: "This Month", value: "this_month" },
    { title: "This Year", value: "this_year" },
    { title: "Custom Range", value: "custom_range" },
  ];

  const handleFilterChange = ({ mode, start, end }) => {
    console.log("Filter 1:", mode, start, end);
    setPeriodSelection({ mode, start, end });
  };

  const {
    busy: tilesBusy,
    setBusy: setTilesBusy,
    attempt: tilesBusyAttempt,
    retry: retryTiles,
  } = useDataApiBusy(`${bearerToken}|${selectedRestaurantId || primaryRestaurantId}`);

  useEffect(() => {
     var FETCH_ALL_TIME_DATA_URL =
  `https://data.tablemate.net/customers?rid=${selectedRestaurantId || primaryRestaurantId}&dcid=2&did=1&p=2&f=&d=${localToday()}&from=&to=`;

    if (!FETCH_ALL_TIME_DATA_URL || !bearerToken) {
      if (!bearerToken) {
        console.log(
          "[HOME ALLTIME] Skipping sessions fetch: no bearerToken yet."
        );
      }
      return;
    }

    const supportsAbortController =
      typeof window !== "undefined" &&
      typeof window.AbortController === "function";

    const controller = supportsAbortController ? new AbortController() : null;
    const signal = controller ? controller.signal : undefined;

    const loadKey = `${bearerToken}|${selectedRestaurantId || primaryRestaurantId}`;
    const background = tilesLoadedFor.current === loadKey;

    async function loadAllTimeData() {
      console.log(
        "[HOME ALLTIME] Attempting to download. Restaurant:",
        selectedRestaurantId || primaryRestaurantId
      );

      try {
        if (background) {
          setTilesRefreshing(true);
        } else {
          setIsQuestionDataLoading(true);
          setTilesUpdatedAt(null);
          tilesUpdatedAtRef.current = null;
        }

        const proxiedUrl = `${REDIR_BASE}${encodeURIComponent(
          FETCH_ALL_TIME_DATA_URL
        )}`;

        console.log("[HOME ALLTIME] Downloading data:", proxiedUrl);

        const res = await fetch(proxiedUrl, {
          signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: bearerToken,
          },
        });

        if (!res.ok) {
          const busyErr = await busyErrorFrom(res);
          if (busyErr) {
            setTilesBusy(busyErr);
            return;
          }
          const text = await res.text();
          console.error(
            "[HOME ALLTIME] Failed to load sessions:",
            res.status,
            text
          );
          // keep defaults (0 / Loading...) so UI still works
          return;
        }

        setTilesBusy(null);
        const data = await res.json();
        console.log("[HOME ALLTIME] All time data result:", data);

        const first = Array.isArray(data) && data.length ? data[0] : {};

        const toNumber = (v) =>
          typeof v === "number" ? v : Number(v ?? 0) || 0;

        setAllTimeRevenue(toNumber(first.TotalRevenue));
        setAllTimeCustomers(toNumber(first.TotalCustomers));
        setTodayRevenue(toNumber(first.TotalRevenueToday));
        setTodayCustomers(toNumber(first.TotalCustomersToday));
        setAllRating(toNumber(first.AverageRating));

        setEmployeeOfMonth(first.EmployeeOfMonth || "Not set");
        setAllTimeMostPopularOrder(first.MostPopularOrder || "No data");
        setTodayMostPopularOrder(first.MostPopularOrderToday || "No data");

        const now = new Date();
        tilesLoadedFor.current = loadKey;
        tilesUpdatedAtRef.current = now;
        setTilesUpdatedAt(now);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Error loading sessions:", err);
        }
      } finally {
        if (!signal || !signal.aborted) {
          setIsQuestionDataLoading(false);
          setTilesRefreshing(false);
        }
      }
    }

    loadAllTimeData();

    return () => {
      if (controller) controller.abort();
    };
    // 🔁 Re-run when auth token or restaurant changes, on retry, and every minute (tilesTick)
  }, [bearerToken, selectedRestaurantId, primaryRestaurantId, tilesBusyAttempt, setTilesBusy, tilesTick]);

  const formatMoney = (val) => {
    const num = typeof val === "number" ? val : Number(val);
    if (!Number.isFinite(num)) return "0.00";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="details-container-page home-page">
      <Navbar pageTitle={pageTitle} onToggleSidebar={true} />

      {tilesBusy && (
        <BusyNotice message={tilesBusy.message} onRetry={retryTiles} />
      )}

      <div className="row home-stat-tiles">
        <div className="col-md-4 home-stat-tile">
          <div>
            <h3>
              <strong>
                BWP{" "}
                {isQuestionDataLoading
                  ? "Loading..."
                  : formatMoney(allTimeRevenue)}
              </strong>
            </h3>
            <p>
              Sales - All Time (BWP{" "}
              {isQuestionDataLoading
                ? "Loading..."
                : formatMoney(todayRevenue)}{" "}
              - Today)
            </p>
          </div>
        </div>

        <div className="col-md-4 home-stat-tile">
          <div>
            <h3>
              <strong>
                {isQuestionDataLoading ? "Loading..." : allTimeCustomers}
              </strong>
            </h3>
            <p>
              Customers - All Time (
              {isQuestionDataLoading ? "Loading..." : todayCustomers} - Today)
            </p>
          </div>
        </div>

        <div className="col-md-4 home-stat-tile">
          <div>
            <h3>
              <strong>
                {isQuestionDataLoading ? (
                  "Loading..."
                ) : (
                  <Stars
                    value={Number(allRating)}
                    onChange={(val) => setAllRating(val)}
                  />
                )}
              </strong>
            </h3>
            <p>Ratings - All Time</p>
          </div>
        </div>
      </div>

      <p
        className="home-refresh-status"
        aria-live="polite"
        style={{ margin: "4px 0 12px", fontSize: "0.8rem", color: "#6b7280", textAlign: "right" }}
      >
        {tilesUpdatedAt
          ? `Last updated ${formatClock(tilesUpdatedAt)}`
          : isQuestionDataLoading
          ? "Loading…"
          : "Not updated yet"}
        {tilesRefreshing ? " · refreshing…" : " · refreshes every minute"}
      </p>

      <div className="home-stat-tile">
        <div className="row">
          <div className="col-md-4">
            <img
              style={{ display: "inline-block", marginRight: "5px" }}
              className="chartnavigationbar-button-icon"
              src={`/Assets/DataCentre/icons/gratuity.png`}
              alt={`icon`}
            />
            <span>
              {isQuestionDataLoading
                ? "Loading..."
                : allTimeMostPopularOrder + "!"}
            </span>
            <p className="home-stat-title">
              <i>- Most Popular Order - All Time</i>
            </p>
          </div>
          <div className="col-md-4">
            <img
              style={{ display: "inline-block", marginRight: "5px" }}
              className="chartnavigationbar-button-icon"
              src={`/Assets/DataCentre/icons/gratuity.png`}
              alt={`icon`}
            />
            <span>
              {isQuestionDataLoading
                ? "Loading..."
                : todayMostPopularOrder + "!"}
            </span>
            <p className="home-stat-title">
              <i>- Most Popular Order - Today</i>
            </p>
          </div>
          <div className="col-md-4">
            <img
              style={{ display: "inline-block", marginRight: "5px" }}
              className="chartnavigationbar-button-icon"
              src={`/Assets/DataCentre/icons/reports.png`}
              alt={`icon`}
            />
            <span>
              {isQuestionDataLoading
                ? "Loading..."
                : employeeOfMonth + "! 🎉"}
            </span>
            <p className="home-stat-title">
              <i>
                - Employee of Month (
                {new Date().toLocaleString("default", { month: "long" })}{" "}
                {new Date().getFullYear()})
              </i>
            </p>
          </div>
        </div>
      </div>

      <div className="home-period-filter">
        <PeriodFilter filter={periodFilter} onChange={handleFilterChange} />
      </div>

      <div className="row home-charts">
        <div className="col-12 home-charts-col">
          <BarChart
            title="Revenue"
            overrideChartCategory="1"
            overrideChartID="1"
            periodSelection={periodSelection}
            height={320}
            hasAdvancedOptions={false}
            preset={revenueOverTime}
            summaryExpandedByDefault={false}
            compact
          />
        </div>
        <div className="col-lg-6 home-charts-col">
          <BarChart
            title="Sales by category"
            overrideChartCategory="1"
            overrideChartID="2"
            periodSelection={periodSelection}
            height={300}
            hasAdvancedOptions={false}
            preset={salesByCategory}
            summaryExpandedByDefault={false}
            compact
          />
        </div>
        <div className="col-lg-6 home-charts-col">
          <BarChart
            title="Average party size by hour"
            overrideChartCategory="3"
            overrideChartID="2"
            periodSelection={periodSelection}
            height={300}
            hasAdvancedOptions={false}
            preset={partySizeByHour}
            summaryExpandedByDefault={false}
            compact
          />
        </div>
        <div className="col-12 home-charts-col">
          <div className="home-table-card">
            <h3 className="home-table-title">Top 10 selling items</h3>
            <BarChart
              title="Top 10 Selling Items"
              overrideChartCategory="1"
              overrideChartType="table"
              overrideChartID="4"
              periodSelection={periodSelection}
              hasAdvancedOptions={false}
              summaryExpandedByDefault={false}
              compact
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
